import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ShoppingBag } from "lucide-react";
import { EASE } from "./anim";
import { NAV_LINKS, CONTACT } from "../lib/nav";
import { useCart } from "@/lib/cart";

const DESKTOP_LINKS = ["Home", "Shop Here", "Pop-Up Shop", "Beauty Rentals", "Events", "Pricing", "About", "FAQ"];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "border-b border-sand/80 bg-paper/90 backdrop-blur-md" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-6 lg:px-10">
          <Link to="/" data-testid="nav-logo" className="group leading-none">
            <span className="font-serif text-[21px] tracking-[0.22em] text-ink transition-colors group-hover:text-charcoal">
              ESSANDTIALS
            </span>
            <span className="mt-1 block text-[8.5px] uppercase tracking-[0.42em] text-fog">
              Retail + Beauty Space
            </span>
          </Link>

          <nav className="hidden items-center gap-5 xl:flex">
            {DESKTOP_LINKS.map((label) => {
              const l = NAV_LINKS.find((n) => n.label === label);
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `text-[10.5px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                      isActive ? "text-ink" : "text-clay hover:text-ink"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="cart-button"
              onClick={() => setCartOpen(true)}
              aria-label="Open shopping bag"
              className="relative flex h-11 w-11 items-center justify-center rounded-full border border-beige/80 bg-white/70 text-ink transition-colors hover:bg-blush"
            >
              <ShoppingBag size={16} strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-ink text-[10px] text-paper"
                >
                  {count}
                </span>
              )}
            </button>
            <Link
              to="/book"
              data-testid="nav-book-button"
              className="btn btn-dark hidden !px-6 !py-3 md:inline-flex"
            >
              Book Your Space
              <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
            <button
              data-testid="nav-menu-button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-beige/80 bg-white/70 text-ink transition-colors hover:bg-blush xl:hidden"
            >
              <span className="flex flex-col gap-[5px]">
                <span className="h-px w-5 bg-ink" />
                <span className="h-px w-5 bg-ink" />
                <span className="h-px w-3 bg-ink" />
              </span>
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-[80] flex flex-col bg-ink text-paper"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <div className="flex h-20 items-center justify-between px-6 lg:px-10">
              <span className="font-serif text-[21px] tracking-[0.22em]">ESSANDTIALS</span>
              <button
                data-testid="nav-close-button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-paper/25 transition-colors hover:bg-paper/10"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="1.2" />
                </svg>
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center overflow-y-auto px-6 lg:px-10">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: EASE, delay: 0.06 + i * 0.04 }}
                >
                  <Link
                    to={l.to}
                    className="group flex items-baseline justify-between border-b border-paper/10 py-3.5"
                  >
                    <span className="font-serif text-3xl uppercase tracking-[0.06em] transition-colors group-hover:text-blush">
                      {l.label}
                    </span>
                    <span className="text-[10px] tracking-[0.3em] text-paper/40">
                      0{i + 1}
                    </span>
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE, delay: 0.06 + NAV_LINKS.length * 0.04 }}
              >
                <Link
                  to="/rules"
                  className="group flex items-baseline justify-between border-b border-paper/10 py-3.5"
                >
                  <span className="font-serif text-3xl uppercase tracking-[0.06em] transition-colors group-hover:text-blush">
                    Policies
                  </span>
                  <span className="text-[10px] tracking-[0.3em] text-paper/40">0{NAV_LINKS.length + 1}</span>
                </Link>
              </motion.div>
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-wrap items-center justify-between gap-4 px-6 pb-10 lg:px-10"
            >
              <div className="text-xs tracking-[0.14em] text-paper/60">
                <p>{CONTACT.address}</p>
                <p className="mt-1">{CONTACT.email}</p>
              </div>
              <Link to="/book" data-testid="nav-mobile-book-button" className="btn btn-blush">
                Book Your Space
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
